import { createRoute, redirect } from "@tanstack/react-router"
import type { ReactNode } from "react"
import { useCallback } from "react"

import type { AccessState } from "../../access/access-state"
import { hasPermission } from "../../access/permissions"
import { JournalScreen } from "../../screens/journal/journal-screen"
import { JournalPrintScreen } from "../../screens/journal/print-screen"
import { EventsScreen } from "../../screens/notifications/events-screen"
import type { JournalSearch } from "../../screens/journal/journal-search"
import { parseJournalSearch } from "../../screens/journal/journal-search"
import type { CabinetPath } from "../../shell/nav-items"
import { CABINET_PATHS, NAV_SECTIONS } from "../../shell/nav-items"
import { ProfileScreen } from "../../screens/profile/profile-screen"
import { BookingRulesScreen } from "../../screens/settings/booking-rules-screen"
import { BranchesScreen } from "../../screens/settings/branches-screen"
import { BranchScreen } from "../../screens/settings/branch-screen"
import { SettingsScreen } from "../../screens/settings/settings-screen"
import { NotificationRulesScreen } from "../../screens/settings/notifications/rules-screen"
import { TemplatesScreen } from "../../screens/settings/templates/templates-screen"
import {
  SummaryLoadScreen,
  SummaryLossesScreen,
} from "../../screens/summary/summary-breakdown-screens"
import { SummaryBookedScreen } from "../../screens/summary/summary-booked-screen"
import { SummaryScreen } from "../../screens/summary/summary-screen"
import { StaffVisibilityScreen } from "../../screens/settings/staff-visibility-screen"
import { RolesScreen } from "../../screens/settings/roles/roles-screen"
import { developerRoutes } from "./developer-routes"
import { directoryRoutes } from "./directory-routes"
import { protectedRoute } from "./protected-route"
import { SectionGuard, SectionStub } from "./section-guard"

/**
 * Разделы кабинета. Каждый — отдельный маршрут с явным адресом: так параллельные агенты
 * правят разные строки этого файла, а не спорят за одну сгенерированную таблицу маршрутов.
 */

/**
 * Куда вести с корня. Не жёстко в журнал: бухгалтеру с одним правом finance.view журнал
 * не открыт, и встречать его отказом на первом же экране — худшее начало рабочего дня.
 */
/**
 * Первый раздел, который человеку открыт. Нужен и стартовой странице, и экрану отказа:
 * «В журнал» бухгалтеру ведёт во второй такой же отказ — у него нет schedule.view.
 */
export function firstAllowedPath(state: AccessState): CabinetPath {
  if (state.kind !== "ready") return CABINET_PATHS.journal
  const allowed = NAV_SECTIONS.find(
    (section) => section.permission === null || hasPermission(state.permissions, section.permission),
  )
  return allowed?.path ?? CABINET_PATHS.support
}

export const cabinetIndexRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: "/",
  beforeLoad: ({ context }) => {
    // Профиль здесь уже загружен: защищённый маршрут-родитель дождался его в своём beforeLoad.
    throw redirect({ to: firstAllowedPath(context.access.getState()), replace: true })
  },
})

export const journalRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: CABINET_PATHS.journal,
  // День, филиал, шаг сетки и фильтры живут В АДРЕСЕ: ссылку на конкретный день
  // пересылают в чате, открывают в соседней вкладке и возвращаются к ней кнопкой «назад».
  validateSearch: parseJournalSearch,
  component: function JournalRouteComponent(): ReactNode {
    const search = journalRoute.useSearch()
    const navigate = journalRoute.useNavigate()

    const onSearchChange = useCallback(
      (patch: JournalSearch) => {
        void navigate({ search: (previous) => ({ ...previous, ...patch }) })
      },
      [navigate],
    )

    const onPrint = useCallback(
      (date: string, branch: string) => {
        void navigate({ to: "/journal/print", search: { date, branch } })
      },
      [navigate],
    )

    return (
      <SectionGuard id="journal">
        <JournalScreen search={search} onSearchChange={onSearchChange} onPrint={onPrint} />
      </SectionGuard>
    )
  },
})

/**
 * 2-2.12 «Печать расписания» — /journal/print?date=…&branch=…. Своя страница, а не окно:
 * печатается лист, а не экран журнала, и у листа своя раскладка (альбомная или книжная).
 */
export const journalPrintRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: "/journal/print",
  validateSearch: parseJournalSearch,
  component: function JournalPrintRouteComponent(): ReactNode {
    const search = journalPrintRoute.useSearch()
    const navigate = journalPrintRoute.useNavigate()
    const onClose = useCallback(() => {
      void navigate({ to: CABINET_PATHS.journal, search: { date: search.date, branch: search.branch } })
    }, [navigate, search.date, search.branch])

    return (
      <SectionGuard id="journal">
        <JournalPrintScreen
          date={search.date ?? new Date().toISOString().slice(0, 10)}
          branchId={search.branch ?? null}
          onClose={onClose}
        />
      </SectionGuard>
    )
  },
})

/**
 * Центр уведомлений (1-Э1.9) — /notifications.
 *
 * ФИЛЬТР И ФИЛИАЛ В АДРЕСЕ НЕ ЖИВУТ, И ЭТО РАЗНИЦА СО ВСЕМИ ОСТАЛЬНЫМИ РАЗДЕЛАМИ.
 * Адрес кабинета хранит состояние, которое ПЕРЕСЫЛАЮТ: «посмотри вот этот день»,
 * «вот эта запись». Лента событий пересылке не поддаётся в принципе — она у каждого
 * своя, потому что адресуется человеку: ссылка «мои дела» в чужих руках покажет чужие
 * дела или пустоту. Поэтому чип фильтра остаётся внутренним состоянием экрана,
 * а филиал берётся из шапки, как и в остальных разделах без своего параметра.
 *
 * ПРАВА У РАЗДЕЛА НЕТ (permission: null в nav-items) — адресность решает сервер построчно.
 * SectionGuard всё равно нужен: он держит полосы, филиал и заголовок раздела.
 */
export const notificationsRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: CABINET_PATHS.notifications,
  component: function NotificationsRouteComponent(): ReactNode {
    return (
      <SectionGuard id="notifications">
        <EventsScreen />
      </SectionGuard>
    )
  },
})

/**
 * Сводка владельца (5-В1) и два её разбора (5-В2, 5-В3).
 *
 * ДЕНЬ И ФИЛИАЛ ЖИВУТ В АДРЕСЕ. Владелец пересылает управляющему ссылку «посмотри,
 * что вчера было на Гагарина», и она обязана открыть у него тот же день и ту же точку.
 * Разборы — отдельные адреса по той же причине: «вот эти три неявки» шлют строкой в чат.
 */
function parseSummarySearch(raw: Record<string, unknown>): {
  date?: string
  branch?: string
} {
  const date = typeof raw["date"] === "string" ? raw["date"] : undefined
  const branch = typeof raw["branch"] === "string" ? raw["branch"] : undefined
  return {
    ...(date === undefined ? {} : { date }),
    ...(branch === undefined ? {} : { branch }),
  }
}

export const summaryRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: CABINET_PATHS.summary,
  validateSearch: parseSummarySearch,
  component: function SummaryRouteComponent(): ReactNode {
    const search = summaryRoute.useSearch()
    const navigate = summaryRoute.useNavigate()
    const onSearchChange = useCallback(
      (patch: { date?: string | undefined; branch?: string | undefined }) => {
        void navigate({
          search: (previous) => {
            // «Сегодня» — это ОТСУТСТВИЕ даты в адресе, а не date: undefined:
            // ключ со значением undefined маршрутизатор в ссылку не пустит.
            const next: { date?: string; branch?: string } = { ...previous }
            if ("date" in patch) {
              if (patch.date === undefined) delete next.date
              else next.date = patch.date
            }
            if ("branch" in patch) {
              if (patch.branch === undefined) delete next.branch
              else next.branch = patch.branch
            }
            return next
          },
        })
      },
      [navigate],
    )
    return (
      <SectionGuard id="summary">
        <SummaryScreen
          search={search}
          onSearchChange={onSearchChange}
          onOpenLoad={() => {
            void navigate({ to: "/summary/load", search })
          }}
          onOpenLosses={() => {
            void navigate({ to: "/summary/cancellations", search })
          }}
          onOpenBooked={() => {
            void navigate({
              to: "/summary/booked",
              search: search.branch === undefined ? {} : { branch: search.branch },
            })
          }}
          onOpenClients={() => {
            void navigate({ to: CABINET_PATHS.clients })
          }}
        />
      </SectionGuard>
    )
  },
})

export const summaryLoadRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: "/summary/load",
  validateSearch: parseSummarySearch,
  component: function SummaryLoadRouteComponent(): ReactNode {
    const search = summaryLoadRoute.useSearch()
    const navigate = summaryLoadRoute.useNavigate()
    return (
      <SectionGuard id="summary">
        <SummaryLoadScreen
          search={search}
          onBack={() => {
            void navigate({ to: CABINET_PATHS.summary, search })
          }}
          onOpenEmployeeDay={(employeeId, branchId, date) => {
            // Разбор кончается разговором с мастером, и начинается он с его дня:
            // журнал открывается на той же дате с фокусом на этом человеке.
            // ФИЛИАЛ БЕРЁТСЯ ИЗ СТРОКИ, а не из адреса сводки: в режиме «Все филиалы»
            // адрес точки не называет, и журнал открывался в той, что попалась первой,
            // — то есть разговор про пустой день начинался с чужого расписания.
            void navigate({
              to: CABINET_PATHS.journal,
              search: { date, master: employeeId, branch: branchId },
            })
          }}
        />
      </SectionGuard>
    )
  },
})

export const summaryLossesRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: "/summary/cancellations",
  validateSearch: parseSummarySearch,
  component: function SummaryLossesRouteComponent(): ReactNode {
    const search = summaryLossesRoute.useSearch()
    const navigate = summaryLossesRoute.useNavigate()
    return (
      <SectionGuard id="summary">
        <SummaryLossesScreen
          search={search}
          onBack={() => {
            void navigate({ to: CABINET_PATHS.summary, search })
          }}
          onOpenAppointment={(appointmentId, branchId, date) => {
            void navigate({
              to: CABINET_PATHS.journal,
              search: {
                date,
                appointment: appointmentId,
                ...(branchId !== null
                  ? { branch: branchId }
                  : search.branch === undefined
                    ? {}
                    : { branch: search.branch }),
              },
            })
          }}
        />
      </SectionGuard>
    )
  },
})

/**
 * 5-В4 «Записано». Период живёт в адресе: «покажи, что записано на две недели» —
 * это ссылка, которую пересылают, а не состояние вкладки.
 */
export const summaryBookedRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: "/summary/booked",
  validateSearch: (raw: Record<string, unknown>): { days?: number; branch?: string } => {
    const days = Number(raw["days"])
    const branch = typeof raw["branch"] === "string" ? raw["branch"] : undefined
    return {
      ...([3, 7, 14].includes(days) ? { days } : {}),
      ...(branch === undefined ? {} : { branch }),
    }
  },
  component: function SummaryBookedRouteComponent(): ReactNode {
    const search = summaryBookedRoute.useSearch()
    const navigate = summaryBookedRoute.useNavigate()
    return (
      <SectionGuard id="summary">
        <SummaryBookedScreen
          search={search}
          singleBranch={search.branch !== undefined}
          onSearchChange={(patch) => {
            void navigate({
              search: (previous) => {
                const next: { days?: number; branch?: string } = { ...previous }
                if (patch.days === undefined) delete next.days
                else next.days = patch.days
                return next
              },
            })
          }}
          onBack={() => {
            void navigate({
              to: CABINET_PATHS.summary,
              search: search.branch === undefined ? {} : { branch: search.branch },
            })
          }}
          onOpenDay={(date) => {
            void navigate({
              to: CABINET_PATHS.journal,
              search: { date, ...(search.branch === undefined ? {} : { branch: search.branch }) },
            })
          }}
        />
      </SectionGuard>
    )
  },
})

export const branchesRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: CABINET_PATHS.branches,
  component: function BranchesRouteComponent(): ReactNode {
    const navigate = branchesRoute.useNavigate()
    return (
      <SectionGuard id="branches">
        <BranchesScreen
          onOpen={(branchId) => {
            void navigate({ to: "/branches/$branchId", params: { branchId } })
          }}
        />
      </SectionGuard>
    )
  },
})

/**
 * Настройки одного филиала (4-Б1). Отдельный адрес, а не вкладка: ссылку «поставь тут
 * шаг 30» отправляют управляющему, и она обязана открыть тот же экран у него.
 */
export const branchRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: "/branches/$branchId",
  component: function BranchRouteComponent(): ReactNode {
    const { branchId } = branchRoute.useParams()
    const navigate = branchRoute.useNavigate()
    return (
      <SectionGuard id="branches">
        <BranchScreen
          branchId={branchId}
          onBack={() => {
            void navigate({ to: CABINET_PATHS.branches })
          }}
        />
      </SectionGuard>
    )
  },
})

export const reportsRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: CABINET_PATHS.reports,
  component: () => (
    <SectionStub
      id="reports"
      summary="Здесь появятся загрузка мастеров, выручка, отмены и неявки."
    />
  ),
})

export const channelsRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: CABINET_PATHS.channels,
  component: () => (
    <SectionStub
      id="channels"
      summary="Здесь появятся каналы уведомлений, шаблоны сообщений и связка с ботами."
    />
  ),
})

export const campaignsRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: CABINET_PATHS.campaigns,
  component: () => <SectionStub id="campaigns" summary="" />,
})

export const auditRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: CABINET_PATHS.audit,
  component: () => <SectionStub id="audit" summary="" />,
})

export const billingRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: CABINET_PATHS.billing,
  component: () => (
    <SectionStub
      id="billing"
      summary="Здесь появятся тариф салона, счета и оплата. Платит салон — с конечных клиентов платформа денег не берёт."
    />
  ),
})

export const settingsRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: CABINET_PATHS.settings,
  component: function SettingsRouteComponent(): ReactNode {
    const navigate = settingsRoute.useNavigate()
    return (
      <SectionGuard id="settings">
        <SettingsScreen
          onOpen={(to) => {
            void navigate({ to })
          }}
        />
      </SectionGuard>
    )
  },
})

export const supportRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: CABINET_PATHS.support,
  component: () => (
    <SectionStub
      id="support"
      summary="Здесь появятся обращения в поддержку и номер обращения для быстрого разбора."
    />
  ),
})

/**
 * Профиль и безопасность (1-Э1.10). Не раздел навигации, а личная страница: права не нужны,
 * свой пароль и свои входы есть у любого, кто вошёл.
 */
export const profileRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: "/profile",
  component: ProfileScreen,
})

/**
 * 4-Б4 «Правила записи» — подраздел настроек со своим адресом: «поставь срок отмены три
 * часа» — обычная просьба в чате, и у неё обязана быть ссылка.
 */
export const bookingRulesRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: "/settings/booking-rules",
  component: () => (
    <SectionGuard id="settings">
      <BookingRulesScreen />
    </SectionGuard>
  ),
})

/**
 * 4-Б6 «Шаблоны сообщений» — подраздел настроек со своим адресом: «поправь текст
 * напоминания» приходит ссылкой, а не описанием, куда нажать.
 */
export const templatesRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: "/settings/templates",
  component: () => (
    // Право `templates.manage`, а не `settings.view`: ровно его требует сервер, и человек
    // с одним `settings.view` иначе видел бы пустой экран с отказами вместо объяснения.
    <SectionGuard id="settings" permission="templates.manage">
      <TemplatesScreen />
    </SectionGuard>
  ),
})

/**
 * 4-Б5 «Уведомления» — матрица «событие × канал» и тихие часы.
 *
 * Своё право, а не `settings.view`: матрица решает, пойдёт ли напоминание по SMS,
 * то есть тратит деньги салона, — и ровно его требует сервер. Администратор за стойкой
 * видел бы иначе пустой экран с отказами вместо объяснения, почему ему сюда нельзя.
 */
export const notificationRulesRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: "/settings/notifications",
  component: () => (
    <SectionGuard id="settings" permission="notifications.rules.manage">
      <NotificationRulesScreen />
    </SectionGuard>
  ),
})

/**
 * 4-Б12 «Что мастер видит о клиенте». Отдельный адрес: ссылку на эту настройку
 * присылают владельцу в разборе «кто открывал телефоны».
 */
export const staffVisibilityRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: "/settings/staff-visibility",
  component: () => (
    <SectionGuard id="settings">
      <StaffVisibilityScreen />
    </SectionGuard>
  ),
})

/**
 * 4-Б11 «Роли и права». Право `roles.manage` — то же, что требует сервер: администратор
 * со `settings.view` увидел бы иначе экран из одних отказов.
 */
export const rolesRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: "/settings/roles",
  component: function RolesRouteComponent(): ReactNode {
    const navigate = rolesRoute.useNavigate()
    return (
      <SectionGuard id="settings" permission="roles.manage">
        <RolesScreen
          onOpenVisibility={() => {
            void navigate({ to: "/settings/staff-visibility" })
          }}
        />
      </SectionGuard>
    )
  },
})

export const sectionRoutes = [
  cabinetIndexRoute,
  summaryRoute,
  summaryLoadRoute,
  summaryLossesRoute,
  summaryBookedRoute,
  notificationsRoute,
  journalRoute,
  journalPrintRoute,
  // Клиенты, каталог, сотрудники, график и первый запуск объявлены отдельным файлом:
  // общий список маршрутов правят несколько агентов сразу, и это единственное место,
  // где они сталкиваются неизбежно.
  ...directoryRoutes,
  branchesRoute,
  branchRoute,
  reportsRoute,
  channelsRoute,
  campaignsRoute,
  auditRoute,
  billingRoute,
  // «Разработчикам» (4-В6/В7/В8) — отдельным файлом, как справочники.
  ...developerRoutes,
  settingsRoute,
  supportRoute,
  profileRoute,
  bookingRulesRoute,
  staffVisibilityRoute,
  rolesRoute,
  templatesRoute,
  notificationRulesRoute,
]
